/*
 * order-status-lookup.js
 *
 * Order lookup frontend, ported from eRepairDirect-original
 * (storefront/components/flow + order-status-lookup + make-your-choice-direct).
 *
 * Re-packaged for the Dawn theme as a single plain-browser asset — no build step,
 * no ES modules. Each original module is wrapped in its own IIFE; the cross-module
 * `import Flow` dependency is resolved through `window.Flow`.
 *
 * Neptune removal: `window.neptune_debug` -> `window.repair_debug`; the client-side
 * `Neptune.liquid` render in MakeYourChoiceDirect is dropped (options are now
 * server-rendered by the make-your-choice-direct snippet); dead `[neptune-liquid]`
 * selectors removed in favour of their existing functional fallbacks.
 *
 * Debug mode: set `window.repair_debug = true` or append `?debug=true` to the URL.
 */

/* ------------------------------------------------------------------ *
 * Flow — form submission + polling of /pages/order/{orderNumber}
 * ------------------------------------------------------------------ */
(function () {
  'use strict';

  const Flow = {
    // Default configuration
    config: {
      formSelectors: ['shopify-forms-embed'],
      maxAttempts: 40,
      pollingInterval: 500, // Default polling interval in milliseconds
      initialDelay: 1000 // Initial delay before first polling attempt
    },

    // Initialize with custom configuration
    init(config = {}) {
      if (config.pollingInterval) {
        // Parse polling interval (should already be in milliseconds)
        config.pollingInterval = parseInt(config.pollingInterval, 10);
        // Fallback if parsing fails or results in an invalid number
        if (isNaN(config.pollingInterval) || config.pollingInterval <= 0) {
          config.pollingInterval = 500; // Use a safe default (500ms)
        }
      }

      if (config.initialDelay) {
        config.initialDelay = parseInt(config.initialDelay, 10);
      }
      if (config.maxAttempts) {
        config.maxAttempts = parseInt(config.maxAttempts, 10);
      }
      this.config = { ...this.config, ...config };
      return this;
    },

    // Main request method - returns a promise that resolves with order data
    request: obj => {
      return new Promise((res, rej) => {
        const requestID = obj.orderNumber;

        Flow._request(requestID, obj)
          .then(requestID => {
            return Flow.poll(requestID);
          })
          .then(response => {
            res(response);
          })
          .catch(error => {
            console.error('[Flow] Polling failed with error:', error);
            rej(error);
          });
      });
    },

    // Debug utility functions
    debug(message, ...args) {
      const debugEnabled = window.repair_debug || new URLSearchParams(window.location.search).has('debug');
      if (debugEnabled) {
        console.log(`[Flow] ${message}`, ...args);
      }
    },

    // Email normalization utility for consistent comparison
    normalizeEmail(email) {
      if (!email || typeof email !== 'string') {
        return email;
      }

      // First decode URL-encoded characters to get the original email
      let normalizedEmail = decodeURIComponent(email);

      return normalizedEmail
        .toLowerCase()         // Convert to lowercase
        .trim();               // Remove leading/trailing whitespace
    },

    // Find the Shopify Form embedded in the page
    findFormEmbed(root = document) {
      const { formSelectors } = Flow.config;
      for (const selector of formSelectors) {
        try {
          const scope = root && typeof root.querySelector === 'function' ? root : document;
          const candidate = scope.querySelector(selector);
          if (candidate) {
            return candidate;
          }
        } catch (error) {
          // Ignore invalid selector errors and continue
          continue;
        }
      }
      return null;
    },

    // Submit the form with order data
    _request: (requestID, obj) => {
      return new Promise((res, rej) => {
        try {
          // Check if this request came from URL parameters (email links)
          // URL parameters should always take priority and bypass sessionStorage checks
          const urlParams = new URLSearchParams(window.location.search);
          const hasUrlParams = urlParams.has('email') && (urlParams.has('order_name') || urlParams.has('order_number'));

          // Normalize the email from the request object for consistent comparison
          const normalizedRequestEmail = Flow.normalizeEmail(obj.email);
          Flow.debug('Request email normalized:', obj.email, '->', normalizedRequestEmail);

          if (hasUrlParams) {
            Flow.debug('URL parameters detected - bypassing sessionStorage checks for email link priority');
            // Clear any existing pending lookup to ensure fresh submission
            sessionStorage.removeItem('pendingOrderLookup');

            // Also clear any order-specific sessionStorage keys that might interfere
            // These could be from older implementations or other components
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              // Clear keys that look like order-specific dismissal keys (e.g., "461666-dismissed-after-submit")
              if (key && (key.includes('-dismissed-') || key.includes('order-') || /^\d+-.+/.test(key))) {
                keysToRemove.push(key);
              }
            }
            keysToRemove.forEach(key => {
              localStorage.removeItem(key);
              Flow.debug(`Cleared interfering sessionStorage key: ${key}`);
            });
          } else {
            // Check if this request was initiated by our own event listener
            // to prevent infinite loops, but only for non-URL parameter requests
            const pendingLookup = sessionStorage.getItem('pendingOrderLookup');
            if (pendingLookup) {
              try {
                const pendingData = JSON.parse(pendingLookup);
                const timeSinceLastSubmission = Date.now() - (pendingData.timestamp || 0);
                const fiveMinutesInMs = 5 * 60 * 1000;

                // If the pending lookup is for the same order and it's recent (less than 5 minutes),
                // skip form submission to prevent infinite loops
                // Normalize both emails for consistent comparison
                const normalizedStoredEmail = Flow.normalizeEmail(pendingData.email);
                Flow.debug('Email comparison - stored:', pendingData.email, '->', normalizedStoredEmail);
                Flow.debug('Email comparison - current:', obj.email, '->', normalizedRequestEmail);

                if (pendingData.orderNumber === obj.orderNumber &&
                  normalizedStoredEmail === normalizedRequestEmail &&
                  timeSinceLastSubmission < fiveMinutesInMs) {
                  Flow.debug('Skipping form submission - recent pending lookup found');
                  res(requestID);
                  return;
                } else {
                  // Clear old/different pending lookup
                  sessionStorage.removeItem('pendingOrderLookup');
                  Flow.debug('Cleared old pending lookup - proceeding with fresh submission');
                }
              } catch (e) {
                // If we can't parse the pending lookup, clear it and proceed
                sessionStorage.removeItem('pendingOrderLookup');
                Flow.debug('Cleared invalid pending lookup data');
              }
            }
          }

          // Process form submission once form is available
          const processFormSubmission = (formEmbed) => {
            // Store request data for polling with normalized email
            sessionStorage.setItem('pendingOrderLookup', JSON.stringify({
              requestID,
              orderNumber: obj.orderNumber,
              email: normalizedRequestEmail,
              timestamp: Date.now()
            }));
            Flow.debug('Stored pendingOrderLookup with normalized email:', normalizedRequestEmail);

            // Set hidden request fields
            const requestIdField = formEmbed.shadowRoot.querySelector('[id="custom#request_id"]');
            if (requestIdField) {
              requestIdField.value = requestID;
              requestIdField.dispatchEvent(new Event('input', { bubbles: true }));
            }

            const requestField = formEmbed.shadowRoot.querySelector('[name="custom#request"]');
            if (requestField) {
              // Submit normalized email in the request object for Shopify Flow
              const normalizedRequestObj = { ...obj, email: normalizedRequestEmail };
              requestField.value = JSON.stringify(normalizedRequestObj);
              requestField.dispatchEvent(new Event('input', { bubbles: true }));
            }

            // Set visible fields
            const emailField = formEmbed.shadowRoot.querySelector('#email');
            if (emailField) {
              // Submit normalized email to match Shopify Flow expectations
              emailField.value = normalizedRequestEmail;
              emailField.dispatchEvent(new Event('input', { bubbles: true }));
            }

            const orderField = formEmbed.shadowRoot.querySelector('[id="custom#order_number"]');
            if (orderField) {
              orderField.value = obj.orderNumber;
              orderField.dispatchEvent(new Event('input', { bubbles: true }));
            }

            // Submit the form
            const form = formEmbed.shadowRoot.querySelector('form');
            if (form) {
              // Use requestSubmit() which properly triggers validation and listeners:
              if (form.requestSubmit) {
                form.requestSubmit();
              } else {
                // Fallback for older browsers - call the handler directly
                const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                form.dispatchEvent(submitEvent);
              }
              res(requestID);
            } else {
              console.error('[Flow] Form not found in shadow root');
              // Still resolve so polling can start
              res(requestID);
            }
          };

          // Wait for form-embed to be available
          const waitForFormEmbed = (attempts = 0, maxAttempts = 10) => {
            // Find the form-embed element
            const formEmbed = Flow.findFormEmbed();

            if (formEmbed && formEmbed.shadowRoot) {
              // Form is available, proceed with submission
              processFormSubmission(formEmbed);
            } else if (attempts < maxAttempts) {
              // Try again after a short delay
              console.info(`[Flow] Waiting for form-embed (attempt ${attempts + 1}/${maxAttempts})`);
              setTimeout(() => waitForFormEmbed(attempts + 1, maxAttempts), 200);
            } else {
              // Max attempts reached, continue with polling anyway
              console.error('[Flow] form-embed or shadow root not found after multiple attempts');
              // Store request data for polling anyway so we can still try to poll
              sessionStorage.setItem('pendingOrderLookup', JSON.stringify({
                requestID,
                orderNumber: obj.orderNumber,
                email: normalizedRequestEmail,
                timestamp: Date.now()
              }));
              Flow.debug('Stored fallback pendingOrderLookup with normalized email:', normalizedRequestEmail);
              res(requestID);
            }
          };

          // Start waiting for the form
          waitForFormEmbed();
        } catch (error) {
          console.error('[Flow] Error in _request:', error);
          rej(error);
        }
      });
    },

    // Poll for results
    poll: (requestID) => {
      return new Promise((res, rej) => {
        try {
          // Get order number and email from requestID and sessionStorage
          const orderNumber = requestID;
          let email = '';
          let pendingData = null;

          try {
            const pendingLookupJSON = sessionStorage.getItem('pendingOrderLookup');
            if (pendingLookupJSON) {
              pendingData = JSON.parse(pendingLookupJSON);
              email = pendingData.email || '';
            }
          } catch (e) {
            console.error('[Flow] Error parsing pendingOrderLookup:', e);
          }

          // Use the current order page URL pattern that matches order-status-lookup component
          const url = `/pages/order/${orderNumber}`;

          let attempts = 0;
          const maxAttempts = Flow.config.maxAttempts;
          const pollingInterval = Flow.config.pollingInterval;
          const errors = [];

          // Single polling function with retry logic
          const pollForData = () => {
            // Increment attempts counter
            attempts++;

            // Check if we've exceeded max attempts
            if (attempts > maxAttempts) {
              console.error('[Flow] All polling attempts failed with errors:', errors);

              // Check if we had responses but no matching order
              const hadResponses = errors.some(error => error.includes('No order content found'));

              if (hadResponses) {
                // We got responses but couldn't find matching order
                rej(new Error(`We couldn't find order #${orderNumber}. Please verify your order number and email address are correct.`));
              } else {
                // General connection or server error
                rej(new Error(`Exceeded max attempts (${maxAttempts}) for order lookup. Please try again later.`));
              }
              return;
            }

            // For the first attempt, add an initial delay to give Shopify Flow time to process
            const initialDelay = Flow.config.initialDelay || 5000;
            const fetchWithDelay = (attempts === 1) ?
              new Promise(resolve => {
                setTimeout(() => {
                  resolve();
                }, initialDelay);
              }) :
              Promise.resolve();

            // Execute fetch after delay (if any)
            fetchWithDelay
              .then(() => fetch(url))
              .then(response => {
                if (!response.ok) {
                  const error = `HTTP error ${response.status} for URL ${url}`;
                  errors.push(error);
                  return Promise.reject(error);
                }
                return response.text();
              })
              .then(html => {
                // Create a temporary container to parse the HTML
                const tempContainer = document.createElement('div');
                tempContainer.innerHTML = html;

                // Find the order content in the fetched HTML
                const orderContent = tempContainer.querySelector('.order-status-details') ||
                  tempContainer.querySelector('.order-status') ||
                  tempContainer.querySelector('.order-lookup-result');

                if (!orderContent) {
                  const error = `No order content found in HTML response from ${url}`;
                  errors.push(error);
                  return Promise.reject(error);
                }

                // Extract order data from the HTML
                const orderData = {
                  orderNumber,
                  customerEmail: email,
                  orderContent: orderContent.outerHTML,
                  timestamp: new Date().toISOString()
                };

                // Try to extract additional data from the HTML if available
                try {
                  // Try to extract order status from HTML
                  const statusElement = orderContent.querySelector('.order-status-label') ||
                    orderContent.querySelector('[data-order-status]');
                  if (statusElement) {
                    orderData.jobCurrentStatus = statusElement.textContent.trim();
                  }

                  // Try to extract order date from HTML
                  const dateElement = orderContent.querySelector('.order-date') ||
                    orderContent.querySelector('[data-order-date]');
                  if (dateElement) {
                    orderData.orderCreatedAt = dateElement.getAttribute('datetime') || dateElement.textContent.trim();
                  }

                  // We've successfully found the order content
                  sessionStorage.removeItem('pendingOrderLookup');

                  // Return the order data
                  res(orderData);

                } catch (extractError) {
                  console.error('[Flow] Error extracting data from HTML:', extractError);

                  // Even with extraction error, we have the order content HTML
                  // so we can still return the basic order data
                  sessionStorage.removeItem('pendingOrderLookup');
                  res(orderData);
                }
              })
              .catch(error => {
                // Suppress logging for expected 404 errors during polling to reduce console noise
                const is404Error = error && error.toString().includes('404');
                if (!is404Error) {
                  console.error('[Flow] Error in polling attempt:', error);
                }
                errors.push(error.message || String(error));

                // Schedule next polling attempt
                setTimeout(pollForData, pollingInterval);
              });
          };

          // Start polling
          pollForData();

        } catch (error) {
          console.error('[Flow] Error in poll method:', error);
          rej(error);
        }
      });
    }
  };

  window.Flow = Flow;
})();

/* ------------------------------------------------------------------ *
 * MakeYourChoiceDirect — estimate-choice (QUOTE_ACCEPT) actions
 * ------------------------------------------------------------------ */
(function () {
  'use strict';

  // Debug utility function
  function debug(message, data) {
    // Check for debug mode in window object or URL parameter
    const isDebugMode = window.repair_debug ||
                      (window.location.search.indexOf('debug=true') !== -1);

    if (isDebugMode) {
      if (data !== undefined) {
        console.log(`[MakeYourChoiceDirect] ${message}`, data);
      } else {
        console.log(`[MakeYourChoiceDirect] ${message}`);
      }
    }
  }

  // Error logging function (always logs errors regardless of debug mode)
  function logError(message, error) {
    console.error(`[MakeYourChoiceDirect] ${message}`, error);
  }

  function parseJobCodesData(data) {
    if (Array.isArray(data)) return data;
    if (typeof data !== 'string' || data.trim() === '') return null;
    try {
      let parsed = JSON.parse(data);
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      return Array.isArray(parsed) ? parsed : null;
    } catch (error) {
      logError('Error parsing job codes data:', error);
      return null;
    }
  }

  // Create the component object
  const MakeYourChoiceDirect = {
    el: null,
    job_codes: [],
    order: null,
    products: {},
    _initialized: false,

    async init(jobCodes, orderNumber) {
      debug('Initializing MakeYourChoiceDirect component');
      debug('Job codes:', jobCodes);
      debug('Order number:', orderNumber);

      // Prevent duplicate initialization
      if (this._initialized) {
        debug('Component already initialized, skipping');
        return;
      }
      this._initialized = true;

      this.el = document.querySelector('.make-your-choice-direct-container');

      // Listen for custom refresh events
      document.addEventListener('make-your-choice:refresh', (event) => {
        debug('Received make-your-choice:refresh event', event.detail);
        this.updateAllProductDisplays();
      });
      if (!this.el) {
        logError('Container element not found.');
        return;
      }

      // Clear cart when component loads - ensures fresh start
      debug('Clearing cart for fresh start');
      if (window.Cart && typeof window.Cart.get === 'function') {
        try {
          // Get current cart to preserve important attributes
          const currentCart = await window.Cart.get();
          const attributesToPreserve = {};

          // Parse job codes to determine if we're in a valid make-your-choice context
          const parsedJobCodes = parseJobCodesData(jobCodes);
          const isValidMakeYourChoiceContext = parsedJobCodes && parsedJobCodes.length > 0;

          debug('Is valid make-your-choice context:', isValidMakeYourChoiceContext);

          // Preserve critical attributes that other components depend on
          if (currentCart.attributes) {
            const criticalAttributes = [
              'Protection Declined',  // Always preserve protection status
              // Only preserve make-your-choice attributes if we're actually in make-your-choice context
              ...(isValidMakeYourChoiceContext ? [
                '_original_order_number',
                '_choice_made',
                '_choice_made_timestamp',
                '_choice_made_option_index'
              ] : [])
            ];

            debug('Attributes to preserve:', criticalAttributes);

            criticalAttributes.forEach(attr => {
              if (currentCart.attributes[attr]) {
                attributesToPreserve[attr] = currentCart.attributes[attr];
              }
            });
          }

          // Clear all cart items but preserve selected attributes
          if (currentCart.items && currentCart.items.length > 0) {
            const updates = {};
            currentCart.items.forEach(item => {
              updates[item.id] = 0; // Set quantity to 0 to remove
            });

            await window.Cart.update(updates);
            debug('Cart items cleared successfully');
          } else {
            debug('Cart already empty, no items to clear');
          }

          // If we're NOT in a make-your-choice context, clear ALL attributes to start fresh
          if (!isValidMakeYourChoiceContext) {
            debug('Not in make-your-choice context, clearing all cart attributes');
            await window.Cart.clear();
            debug('All cart attributes cleared for fresh start');

            // Restore only the Protection Declined status if it existed
            if (attributesToPreserve['Protection Declined']) {
              await window.Cart.attributes({ 'Protection Declined': attributesToPreserve['Protection Declined'] });
              debug('Protection Declined status preserved:', attributesToPreserve['Protection Declined']);
            }
          } else {
            // Restore preserved attributes if any were found
            if (Object.keys(attributesToPreserve).length > 0) {
              await window.Cart.attributes(attributesToPreserve);
              debug('Critical cart attributes preserved:', attributesToPreserve);
            }
          }
        } catch (error) {
          logError('Error clearing cart:', error);
          // Continue anyway - don't block the component
        }
      } else {
        debug('Cart.get not available, continuing without clearing');
      }

      const parsedJobCodes = parseJobCodesData(jobCodes);

      if (!parsedJobCodes || parsedJobCodes.length === 0) {
        this.el.innerHTML = '<div class="text-center py-8"><p class="text-gray-500">No estimate options available for this order.</p></div>';
        return;
      }

      this.job_codes = parsedJobCodes;
      this.order = orderNumber;

      debug('Initializing component with job codes:', this.job_codes);

      // Options are server-rendered by the make-your-choice-direct snippet (Neptune.liquid
      // client render removed). Load product data and populate the rendered markup.
      await this.loadAllProducts();
      debug('All products loaded, updating displays');
      this.updateAllProductDisplays();
    },

    recall() {},

    store() {},

    // Using inline onclick handler instead of setupFormSubmission
    async handleFormSubmit(event) {
      event.preventDefault();
      debug('Form submit handler called');

      // Get the feedback element
      const feedbackEl = document.getElementById('myc-feedback');

      // Helper function to show feedback messages
      const showFeedback = (message, isError = false) => {
        if (!feedbackEl) return;

        feedbackEl.textContent = message;
        feedbackEl.className = `mt-4 p-3 rounded-md ${isError ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`;
        feedbackEl.classList.remove('hidden');
      };

      // Disable the submit button during processing
      const submitButton = event.target;
      submitButton.disabled = true;
      submitButton.textContent = 'Adding to cart...';

      try {
        // Find the selected radio button
        const selectedRadio = document.querySelector('input[name="estimate-option"]:checked');
        if (!selectedRadio) {
          showFeedback('Please select an option', true);
          return;
        }

        const optionIndex = parseInt(selectedRadio.value, 10);
        const selectedJobCodeOption = this.job_codes[optionIndex];

        if (!selectedJobCodeOption || !selectedJobCodeOption.products || !selectedJobCodeOption.products.length) {
          showFeedback('Invalid option selected', true);
          return;
        }

        const skus = selectedJobCodeOption.products;
        debug('Processing SKUs for selected option:', skus);

        debug('Adding cart attributes for order reconciliation');
        try {
          const attributesResponse = await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              attributes: {
                '_original_order_number': this.order,
                '_choice_made': selectedJobCodeOption.label || 'Selected Option',
                '_choice_made_timestamp': new Date().toISOString(),
                '_choice_made_option_index': optionIndex.toString()
              }
            })
          });

          if (!attributesResponse.ok) {
            const attributesError = await attributesResponse.json();
            logError('Error adding cart attributes:', attributesError);
          } else {
            const attributesData = await attributesResponse.json();
            debug('Cart attributes added successfully:', attributesData);
          }
        } catch (attributesError) {
          logError('Exception adding cart attributes:', attributesError);
        }

        // Map SKUs to variant IDs using the product data we've loaded
        const notFoundSkus = [];
        const items = skus.map(sku => {
          const product = this.products[sku];
          if (!product) {
            logError(`Product not found for SKU: ${sku}`);
            notFoundSkus.push(sku);
            return null;
          }

          if (product.error) {
            logError(`Error for SKU ${sku}: ${product.error}`);
            notFoundSkus.push(sku);
            return null; // Skip this item if there's an error
          }

          debug(`Adding product to cart - SKU: ${sku}, ID: ${product.id}`);
          return {
            id: product.id,
            quantity: 1,
            properties: {
              _sku: sku,
              _order_number: this.order,
              _option_label: selectedJobCodeOption.label || 'Selected Option'
            }
          };
        }).filter(Boolean); // Remove any null items

        if (items.length === 0) {
          if (notFoundSkus.length > 0) {
            showFeedback(`Products not found for SKUs: ${notFoundSkus.join(', ')}. Please contact customer service.`, true);
            return;
          } else {
            showFeedback('No valid products found for the selected option', true);
            return;
          }
        }

        // If some products were found but others weren't, we can still proceed with what we have
        if (notFoundSkus.length > 0) {
          debug(`Proceeding with partial cart add. SKUs not found: ${notFoundSkus.join(', ')}`);
          showFeedback(`Some products were not found (${notFoundSkus.join(', ')}). Adding available products to cart...`);
        }

        // Add items to cart
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          const errorData = await response.json();
          showFeedback(errorData.description || `Error adding items to cart: ${response.status}`, true);
          return;
        }

        // Success - redirect to cart
        showFeedback('Products added to cart! Redirecting...');
        setTimeout(() => {
          window.location.href = '/checkout';
        }, 250);
      } catch (error) {
        logError(`Error adding items to cart: ${error}`);
        showFeedback(`An unexpected error occurred: ${error.message}`, true);
        submitButton.textContent = 'Add Selected Option to Cart';
      }
    },

    async loadAllProducts() {
      const allSkus = this.job_codes.map(choice => choice.products).flat();
      const uniqueSkus = [...new Set(allSkus)];
      const skusToFetch = uniqueSkus.filter(sku => !this.products[sku]);
      if (skusToFetch.length > 0) {
        const promises = skusToFetch.map(sku => this.findProductBySku(sku));
        await Promise.all(promises);
      }
    },

    async findProductBySku(sku) {
      if (!sku) {
        debug('Empty SKU provided to findProductBySku');
        return { error: 'Invalid SKU' };
      }

      // Check if we've already fetched this product
      if (this.products[sku]) {
        debug(`Using cached product data for SKU: ${sku}`);
        return this.products[sku];
      }

      debug(`Looking up product for SKU: ${sku}`);

      try {
        // Shopify search page API
        debug(`Using search page API for SKU ${sku}`);
        const searchPageUrl = `/search?q=${encodeURIComponent(sku)}&type=product&options[prefix]=last&options[unavailable_products]=hide&view=ajax`;
        debug(`Fetching: ${searchPageUrl}`);

        const searchPageResponse = await fetch(searchPageUrl);
        if (searchPageResponse.ok) {
          const searchPageHtml = await searchPageResponse.text();
          debug(`Search page response length: ${searchPageHtml.length} characters`);

          // Look for product handles in the response
          const productHandleRegex = /\/products\/([\w-]+)/g;
          const productHandles = new Set();
          let match;

          while ((match = productHandleRegex.exec(searchPageHtml)) !== null) {
            productHandles.add(match[1]);
          }

          debug(`Found ${productHandles.size} product handles in search page results:`, [...productHandles]);

          // Check each product for the SKU
          for (const handle of productHandles) {
            const productUrl = `/products/${handle}.js`;
            debug(`Fetching product details: ${productUrl}`);

            const productResponse = await fetch(productUrl);
            if (productResponse.ok) {
              const fullProduct = await productResponse.json();
              debug(`Full product data for ${handle}:`, fullProduct);

              if (fullProduct && fullProduct.variants) {
                // First try to find an exact SKU match
                let variant = fullProduct.variants.find(v => v.sku === sku);

                // If no exact match, try case-insensitive match
                if (!variant && sku) {
                  const skuLower = sku.toLowerCase();
                  variant = fullProduct.variants.find(v => v.sku && v.sku.toLowerCase() === skuLower);
                }

                if (variant) {
                  debug(`Found matching variant for SKU ${sku}:`, variant);
                  this.products[sku] = {
                    id: variant.id,
                    product_title: fullProduct.title,
                    sku: sku,
                    price: variant.price, // Extract price from variant
                    handle: handle,
                    variant_title: variant.title !== 'Default Title' ? variant.title : ''
                  };
                  debug(`Found product for SKU ${sku} with price: ${variant.price}`);
                  return this.products[sku];
                } else {
                  debug(`No variant with SKU ${sku} found in product ${handle}`);
                }
              }
            } else {
              debug(`Failed to fetch product details for ${handle}: ${productResponse.status}`);
            }
          }
        } else {
          debug(`Search page request failed with status ${searchPageResponse.status}`);
        }

        // If we reach here, no product was found with the given SKU
        debug(`No products found for SKU ${sku} after trying all approaches`);
        this.products[sku] = {
          error: 'Product not found',
          sku: sku,
          product_title: `SKU: ${sku} (Not Found)`
        };
        return this.products[sku];
      } catch (error) {
        // Keep essential error logging
        logError(`Error fetching product for ${sku}:`, error);
        this.products[sku] = {
          error: error.message || 'Fetch Error',
          sku: sku,
          product_title: `SKU: ${sku} (Error)`
        };
        return this.products[sku];
      }
    },

    updateAllProductDisplays() {
      debug('Updating product displays with:', this.products);

      // Update product displays
      Object.keys(this.products).forEach(sku => {
        const product = this.products[sku];
        if (!product) {
          debug(`No product data for SKU ${sku}`);
          return;
        }

        // Use document.querySelectorAll without the this.el context restriction
        const elements = document.querySelectorAll(`[data-sku-display="${sku}"]`);
        debug(`Found ${elements.length} elements for SKU ${sku}`);

        elements.forEach(element => {
          // Always update the product title, whether found or not
          element.textContent = product.product_title || `SKU: ${sku}`;

          // Find the price element (sibling of the product name element)
          const productItem = element.closest('.product-item');
          if (!productItem) {
            debug(`Could not find parent .product-item for SKU ${sku}`);
            return;
          }

          const priceElement = productItem.querySelector('.product-price');
          if (!priceElement) {
            debug(`Could not find .product-price element for SKU ${sku}`);
            return;
          }

          // Update price if available, otherwise clear it
          if (product.price && !product.error) {
            // Format price as currency
            const formattedPrice = new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD'
            }).format(product.price / 100); // Shopify prices are in cents

            priceElement.textContent = formattedPrice;
          } else {
            priceElement.textContent = '';
          }
        });
      });

      // Enable the submit button - search in the entire document
      const submitButton = document.querySelector('button[type="submit"]');
      debug('Submit button found:', submitButton);

      if (submitButton) {
        submitButton.disabled = false;
        debug('Submit button enabled');
      }
    }
  };

  // Expose the component to the global scope so it can be accessed by inline onclick handlers
  window.MakeYourChoiceDirect = MakeYourChoiceDirect;
})();

/* ------------------------------------------------------------------ *
 * OrderStatusLookup — <order-status-lookup> custom element
 * (depends on window.Flow)
 * ------------------------------------------------------------------ */
(function () {
  'use strict';

  const Flow = window.Flow;

  // Debug utility functions
  function debug(message, ...args) {
    const debugEnabled = window.repair_debug || new URLSearchParams(window.location.search).has('debug');
    if (debugEnabled) {
      console.log(`[OrderStatusLookup] ${message}`, ...args);
    }
  }

  function logError(message, ...args) {
    console.error(`[OrderStatusLookup] ${message}`, ...args);
  }

  // Constants
  const CLASSES = {
    LOADING: 'loading',
    ERROR: 'error',
    CONTENT: 'content'
  };

  /**
   * OrderStatusLookup Component
   *
   * This component handles order status lookup functionality. It focuses on form
   * handling and data fetching; the result markup is server-rendered by the
   * order_lookup_result metaobject template and swapped into the results container.
   *
   * use &debug=true to enable debug mode
   *
   * @element order-status-lookup
   */
  class OrderStatusLookup extends HTMLElement {
    constructor() {
      super();
      // Constructor should only set up properties, not do DOM work
    }

    connectedCallback() {
      // Find results container and error container
      this.resultsContainer = document.querySelector('.order-status-results') ||
                            document.querySelector('#order-status-container') ||
                            document.querySelector('.order-status-details') ||
                            this;

      this.errorContainer = document.querySelector('.order-status-error') ||
                            document.querySelector('#order-status-error') ||
                            this.resultsContainer;

      // Hide the results container initially to prevent showing a blank state
      if (this.resultsContainer) {
        this.resultsContainer.style.display = 'none';
        debug('Initial results container hidden to prevent blank state');
      }

      // Initialize Flow component
      this.initializeFlow();

      // Set up form handling
      this.setupFormHandling();

      // Check for pending lookup from sessionStorage
      this.checkForPendingLookup();

      // Listen for make-your-choice-direct components to be fully hydrated
      this.setupMakeYourChoiceDirectListener();

      // Check URL parameters for auto-submit
      this.checkUrlParametersAndAutoSubmit();

      // If content is already rendered on the page (e.g. from a direct metaobject page load),
      // find and initialize the nested components
      if (this.resultsContainer && this.resultsContainer.querySelector('.order-history')) {
        this._initializeNestedComponents(this.resultsContainer);
      }
    }

    initializeFlow() {
      // Read polling settings from data attributes
      const pollingInterval = this.getAttribute('data-polling-interval');
      const maxAttempts = this.getAttribute('data-max-attempts');

      // Parse polling interval (already in milliseconds from theme)
      let pollingIntervalMs = 500; // Default fallback
      if (pollingInterval) {
        const parsedValue = parseInt(pollingInterval, 10);
        if (!isNaN(parsedValue) && parsedValue > 0) {
          pollingIntervalMs = parsedValue;
        }
      }

      // Parse max attempts
      let maxAttemptsValue = 40; // Default fallback
      if (maxAttempts) {
        const parsedValue = parseInt(maxAttempts, 10);
        if (!isNaN(parsedValue) && parsedValue > 0) {
          maxAttemptsValue = parsedValue;
        }
      }

      debug(`Initializing Flow with pollingInterval: ${pollingIntervalMs}ms, maxAttempts: ${maxAttemptsValue}`);

      this.flowInstance = Flow.init({
        formSelectors: ['shopify-forms-embed'],
        maxAttempts: maxAttemptsValue,
        pollingInterval: pollingIntervalMs,
        initialDelay: 1000
      });
    }

    setupFormHandling() {
      // Try to find and attach to the shadow DOM form directly
      this.attachToShadowDomForm();

      // Check for URL parameters for initial form if it exists
      this.checkUrlParametersForExistingForm();

      // Set up a MutationObserver to watch for dynamically added forms
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            for (const node of mutation.addedNodes) {
              if (node.nodeType === Node.ELEMENT_NODE) {
                if (node.tagName && (node.tagName.toLowerCase() === 'shopify-forms-embed')) {
                  // Wait for the custom element to be defined, then attach
                  window.customElements.whenDefined('shopify-forms-embed').then(() => {
                    this.attachToShadowDomForm(node);
                    // Check for URL parameters when new form is added
                    this.checkUrlParametersForForm(node);
                  });
                }
              }
            }
          }
        }
      });

      // Start observing the document body
      observer.observe(document.body, { childList: true, subtree: true });

      // Shopify Forms builds its <form> inside the shadow DOM asynchronously — often
      // after this element connects. The single attach above can run too early (it
      // no-ops when the shadow form isn't built yet), and the MutationObserver only
      // sees a newly-ADDED <shopify-forms-embed> node, not the form being constructed
      // inside an embed that already exists. Without this, the submit handler may never
      // bind and the form falls through to Shopify's default "form submitted" message.
      // Poll briefly until the (idempotent) bind succeeds.
      this._ensureFormBound();
    }

    /**
     * Reliably bind the submit handler once Shopify Forms has built the shadow-DOM form.
     * attachToShadowDomForm() is idempotent, so re-running it is safe.
     */
    _ensureFormBound(attempts = 0, maxAttempts = 50) {
      const embed = document.querySelector('shopify-forms-embed');
      const form = embed && embed.shadowRoot
        ? embed.shadowRoot.querySelector('form, [data-testid="form"]')
        : null;

      if (form && form._orderLookupHandler) {
        debug(`Submit handler bound after ${attempts} attempt(s)`);
        return;
      }

      // (Re)attempt the bind; no-ops if already bound or the form isn't ready yet.
      this.attachToShadowDomForm();

      if (attempts < maxAttempts) {
        setTimeout(() => this._ensureFormBound(attempts + 1, maxAttempts), 200);
      } else {
        debug(`Gave up binding submit handler after ${maxAttempts} attempts`);
      }
    }

    attachToShadowDomForm(formEmbed = null) {
      // If no specific form-embed is provided, look for any in the document
      if (!formEmbed) {
        formEmbed = document.querySelector('shopify-forms-embed');
      }

      if (!formEmbed || !formEmbed.shadowRoot) {
        return;
      }

      // Find the form in the shadow root
      const form = formEmbed.shadowRoot.querySelector('form, [data-testid="form"]');
      if (form) {

        // Use a named function so we can remove it if needed
        const handleSubmit = (event) => {

          // Check if this is a programmatic submission from Flow
          const pendingLookup = sessionStorage.getItem('pendingOrderLookup');
          if (pendingLookup) {
            return; // Let the normal form submission happen
          }

          event.preventDefault();
          event.stopPropagation();

          // Find the inputs
          const emailInput = form.querySelector('#email, [data-testid="field-email"]');
          const orderInput = form.querySelector('[id="custom#order_number"], [data-testid="field-custom#order_number"]');

          if (emailInput && orderInput) {
            const email = emailInput.value.trim();
            const orderNumber = orderInput.value.trim();

            if (email && orderNumber) {

              // Store the request data in sessionStorage first to prevent infinite loops
              const requestID = `${orderNumber}`;
              sessionStorage.setItem('pendingOrderLookup', JSON.stringify({
                requestID,
                orderNumber,
                email,
                timestamp: Date.now()
              }));

              this.showLoading();
              if (!this.flowInstance) {
                this.initializeFlow();
              }

              // Use Flow.request() which handles both form submission and polling
              this.flowInstance.request({
                orderNumber: orderNumber,
                email: email
              })
                .then(orderData => {
                  this.showContent(orderData);
                })
                .catch(error => {
                  this.showError(error.message || 'Failed to retrieve order status');
                });
            }
          }
        };

        // Store the handler on the form to avoid duplicate listeners
        if (!form._orderLookupHandler) {
          form._orderLookupHandler = handleSubmit;
          form.addEventListener('submit', form._orderLookupHandler);
        }
      } else {
        console.warn('[OrderStatusLookup] No form found in shadow root');
      }
    }

    processOrderLookup(orderNumber, email) {
      this.showLoading();

      const cleanOrderNumber = orderNumber.replace('#', '');

      // Ensure Flow is initialized with proper config
      if (!this.flowInstance) {
        this.initializeFlow();
      }

      this.flowInstance.request({
        orderNumber: cleanOrderNumber,
        email: email
      })
      .then(orderData => {
        this.showContent(orderData);
      })
      .catch(error => {
        this.showError(error.message || 'Failed to retrieve order status');
      });
    }

    checkForPendingLookup() {
      debug('Inside checkForPendingLookup...');
      const pendingLookupJSON = sessionStorage.getItem('pendingOrderLookup');
      if (!pendingLookupJSON) {
        debug('No pending lookup found in sessionStorage.');
        return;
      }

      // Clear any stale pending lookup so a page reload starts fresh.
      sessionStorage.removeItem('pendingOrderLookup');
    }

    /**
     * Ensures the results container is found and visible
     * @param {string} state - The state being shown (for debugging)
     * @returns {boolean} - Whether the container was found
     */
    ensureResultsContainerVisible(state) {
      // Re-attempt to find the container if it's not already set
      if (!this.resultsContainer) {
        this.resultsContainer = document.querySelector('.order-status-results') ||
                              document.querySelector('#order-status-container') ||
                              document.querySelector('.order-status-details') ||
                              this;
      }

      // Make sure the results container is visible
      if (this.resultsContainer) {
        this.resultsContainer.style.display = 'block';
        debug(`Results container shown for ${state} state`);
        return true;
      }

      return false;
    }

    /**
     * Show loading state in the UI
     * Safely handles the case when resultsContainer is not found
     */
    showLoading() {
      // Ensure results container is visible
      this.ensureResultsContainerVisible('loading');

      if (!this.resultsContainer) {
        console.warn('[OrderStatusLookup] Results container not found, creating one');
        const newContainer = document.createElement('div');
        newContainer.className = 'order-status-results';
        document.body.appendChild(newContainer);
        this.resultsContainer = newContainer;
      }

      this.hideInitialForm();

      try {
        // Set appropriate classes
        this.resultsContainer.classList.add(CLASSES.LOADING);
        this.resultsContainer.classList.remove(CLASSES.ERROR, CLASSES.CONTENT);

        // Check if we have a loading indicator element
        let loadingIndicator = this.resultsContainer.querySelector('.loading-state');

        // If no loading indicator exists, create one
        if (!loadingIndicator) {
          loadingIndicator = document.createElement('div');
          loadingIndicator.className = 'loading-indicator';
          loadingIndicator.setAttribute('role', 'status');
          loadingIndicator.setAttribute('aria-live', 'polite');

          // Create loading spinner
          const spinner = document.createElement('div');
          spinner.className = 'loading-spinner';
          spinner.innerHTML = `
            <svg class="spinner" viewBox="0 0 50 50" role="img" aria-label="Loading">
              <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
            </svg>
          `;

          // Create loading text
          const loadingText = document.createElement('div');
          loadingText.className = 'loading-text';
          loadingText.textContent = 'Looking up your order...';

          // Add spinner and text to loading indicator
          loadingIndicator.appendChild(spinner);
          loadingIndicator.appendChild(loadingText);

          // Add loading indicator to results container
          this.resultsContainer.appendChild(loadingIndicator);
        }

        // Make sure the loading indicator is visible
        loadingIndicator.style.display = 'flex';
        loadingIndicator.style.flexDirection = 'column';
        loadingIndicator.style.alignItems = 'center';
        loadingIndicator.style.justifyContent = 'center';
        loadingIndicator.style.padding = '2rem';
        loadingIndicator.style.textAlign = 'center';
      } catch (error) {
        console.error('[OrderStatusLookup] Error updating loading state:', error);
      }
    }

    /**
     * Show error state in the UI
     * @param {string} message - The error message to display
     */
    showError(message) {
      // Ensure results container is visible
      this.ensureResultsContainerVisible('error');

      if (!this.errorContainer) {
        this.errorContainer = document.querySelector('.order-status-error') ||
                              document.querySelector('#order-status-error') ||
                              this.resultsContainer;
      }

      if (!this.resultsContainer) {
        console.warn('[OrderStatusLookup] Results container not found, cannot show error state');
        return;
      }

      if (!this.errorContainer) {
        console.warn('[OrderStatusLookup] Error container not found, falling back to results container');
        this.errorContainer = this.resultsContainer;
      }

      try {
        // Set appropriate classes
        this.resultsContainer.classList.add(CLASSES.ERROR);
        this.resultsContainer.classList.remove(CLASSES.LOADING, CLASSES.CONTENT);

        // Clear any existing error messages to prevent duplicates
        const existingErrorMessages = this.errorContainer.querySelectorAll('.error-message');
        if (existingErrorMessages.length > 0) {
          existingErrorMessages.forEach(function(element) {
            element.remove();
          });
        }

        // Create a new error message element
        const errorElement = document.createElement('div');
        errorElement.className = 'error-message';
        errorElement.setAttribute('role', 'alert');
        errorElement.setAttribute('aria-live', 'assertive');

        // Format the message based on error type
        if (message && message.includes("verify your order number and email")) {
          // This is a "no matching order found" error
          const titleDiv = document.createElement('div');
          titleDiv.className = 'error-title';
          titleDiv.textContent = 'Order Not Found';

          const detailsDiv = document.createElement('div');
          detailsDiv.className = 'error-details';
          detailsDiv.textContent = message;

          const helpDiv = document.createElement('div');
          helpDiv.className = 'error-help';
          helpDiv.textContent = "Please check that you've entered the correct order number and email address.";

          errorElement.appendChild(titleDiv);
          errorElement.appendChild(detailsDiv);
          errorElement.appendChild(helpDiv);
        } else if (message && message.includes("Exceeded max attempts")) {
          // This is a timeout/server error
          const titleDiv = document.createElement('div');
          titleDiv.className = 'error-title';
          titleDiv.textContent = 'Lookup Timeout';

          const detailsDiv = document.createElement('div');
          detailsDiv.className = 'error-details';
          detailsDiv.textContent = message;

          const helpDiv = document.createElement('div');
          helpDiv.className = 'error-help';
          helpDiv.textContent = "Our system is busy. Please try again in a few minutes.";

          errorElement.appendChild(titleDiv);
          errorElement.appendChild(detailsDiv);
          errorElement.appendChild(helpDiv);
        } else {
          // Generic error
          const titleDiv = document.createElement('div');
          titleDiv.className = 'error-title';
          titleDiv.textContent = 'Error';

          const detailsDiv = document.createElement('div');
          detailsDiv.className = 'error-details';
          detailsDiv.textContent = message || 'An error occurred while looking up your order. Please try again.';

          errorElement.appendChild(titleDiv);
          errorElement.appendChild(detailsDiv);
        }

        // Add the error element to the container
        this.errorContainer.appendChild(errorElement);
      } catch (error) {
        console.error('[OrderStatusLookup] Error updating error state:', error);
      }
    }

    /**
     * Display order status content using Liquid template rendering
     * @param {Object} orderData - The order data to display
     */
    showContent(orderData) {
      try {
        // Ensure results container is visible
        this.ensureResultsContainerVisible('content');

        // Hide the initial form after successful lookup
        this.hideInitialForm();

        // Dispatch a custom event to notify that order status lookup was initiated
        const updateEvent = new CustomEvent('order-status:updated', {
          bubbles: true,
          detail: { orderNumber: orderData.orderNumber, data: orderData }
        });
        this.dispatchEvent(updateEvent);

        // Directly fetch the order content from the Liquid template
        this._fetchOrderContent(orderData);

      } catch (error) {
        console.error('[OrderStatusLookup] Error in showContent:', error);
        this.showError('An unexpected error occurred. Please try again.');
      }
    }

    /**
     * Hide loading state in the UI
     * Safely handles the case when resultsContainer is not found
     */
    hideLoading() {
      // Re-attempt to find the container if it's not already set
      if (!this.resultsContainer) {
        this.resultsContainer = document.querySelector('.order-status-results') ||
                              document.querySelector('#order-status-container') ||
                              document.querySelector('.order-status-details') ||
                              this;
      }

      if (!this.resultsContainer) {
        console.warn('[OrderStatusLookup] Results container not found, cannot hide loading state');
        return;
      }

      try {
        // Remove the loading class
        this.resultsContainer.classList.remove(CLASSES.LOADING);

        // Find and hide the loading indicator element
        const loadingIndicator = this.resultsContainer.querySelector('.loading-indicator');
        if (loadingIndicator) {
          loadingIndicator.style.display = 'none';
        }

        // Also hide the main loading state element from the template
        const loadingState = this.querySelector('.loading-state');
        if (loadingState) {
          loadingState.style.display = 'none';
        }

        // Hide Shadow DOM spinner from Shopify Forms app
        this.hideShadowDOMSpinner();
      } catch (error) {
        console.error('[OrderStatusLookup] Error hiding loading state:', error);
      }
    }

    /**
     * Hide the initial form-embed element after a successful order lookup
     */
    hideInitialForm() {
      try {
        debug('Hiding initial form after successful lookup');

        const formEmbed = document.querySelector('shopify-forms-embed');
        // Hide the embed's OWN app-block wrapper. There can be several .shopify-app-block on
        // the page, so target the one that actually contains the form (not document's first).
        const formBlock = formEmbed
          ? formEmbed.closest('.shopify-app-block')
          : document.querySelector('.shopify-app-block');

        const hide = (el) => {
          if (el && el.style.display !== 'none') el.style.display = 'none';
        };
        hide(formBlock);
        hide(formEmbed);
        debug(formEmbed ? 'Form embed + app block hidden' : 'Form embed not found');

        // SR-127 enhancement (beyond live parity): Shopify Forms re-shows its embed when it
        // paints its own "form submitted" success state, which would flash over our loading /
        // result UI until the lookup resolves. Keep it hidden for the rest of the lookup by
        // re-hiding on any attribute change that makes it visible again. The `display !== 'none'`
        // guard in hide() prevents the observer from looping on its own mutations.
        if (formEmbed && !this._formHiddenObserver) {
          this._formHiddenObserver = new MutationObserver(() => {
            hide(formEmbed);
            hide(formEmbed.closest('.shopify-app-block'));
          });
          const opts = { attributes: true, attributeFilter: ['style', 'class', 'hidden'] };
          this._formHiddenObserver.observe(formEmbed, opts);
          if (formBlock) this._formHiddenObserver.observe(formBlock, opts);
        }
      } catch (error) {
        console.error('[OrderStatusLookup] Error hiding initial form:', error);
      }
    }

    /**
     * Hide the loading spinner in the Shopify Forms Shadow DOM
     */
    hideShadowDOMSpinner() {
      try {
        const formEmbed = document.querySelector('shopify-forms-embed');
        if (formEmbed && formEmbed.shadowRoot) {
          // Common selectors for Shopify Forms loading states
          const spinnerSelectors = [
            '.loading',
            '.spinner',
            '.loading-spinner',
            '[data-loading]',
            '.form-loading',
            '.submit-loading',
            '.loading-state',
            '.loading-indicator'
          ];

          for (const selector of spinnerSelectors) {
            const spinner = formEmbed.shadowRoot.querySelector(selector);
            if (spinner) {
              spinner.style.display = 'none';
              spinner.style.visibility = 'hidden';
              spinner.classList.add('hidden');
            }
          }

          // Also try to find and hide any submit button loading states
          const submitButton = formEmbed.shadowRoot.querySelector('button[type="submit"], input[type="submit"]');
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.classList.remove('loading', 'disabled');
          }
        }
      } catch (error) {
        console.error('[OrderStatusLookup] Error hiding Shadow DOM spinner:', error);
      }
    }

    /**
     * Hide error state in the UI
     * Safely handles the case when resultsContainer is not found
     */
    hideError() {
      // Re-attempt to find the container if it's not already set
      if (!this.resultsContainer) {
        this.resultsContainer = document.querySelector('.order-status-results') ||
                              document.querySelector('#order-status-container') ||
                              document.querySelector('.order-status-details') ||
                              this;
      }

      if (!this.resultsContainer) {
        console.warn('[OrderStatusLookup] Results container not found, cannot hide error state');
        return;
      }

      try {
        this.resultsContainer.classList.remove(CLASSES.ERROR);
      } catch (error) {
        console.error('[OrderStatusLookup] Error hiding error state:', error);
      }
    }

    /**
     * Fetches the order content from the Liquid template
     * @param {Object} orderData - The order data to display
     * @private
     */
    _fetchOrderContent(orderData) {
      try {
        // Ensure we have a results container
        if (!this.resultsContainer) {
          this.resultsContainer = document.querySelector('.order-status-results') ||
                                document.querySelector('#order-status-container') ||
                                document.querySelector('.order-status-details') ||
                                this;
        }

        // Construct the URL to the order page
        const orderPageUrl = `/pages/order/${orderData.order_number || orderData.orderNumber}`;

        // Dispatch an event to notify that we're fetching order content
        const fetchEvent = new CustomEvent('order-status:fetch', {
          bubbles: true,
          detail: { orderNumber: orderData.orderNumber, url: orderPageUrl }
        });
        this.dispatchEvent(fetchEvent);

        // Fetch the order page content
        fetch(orderPageUrl)
          .then(response => {
            if (!response.ok) {
              throw new Error(`Failed to fetch order content: ${response.status} ${response.statusText}`);
            }
            return response.text();
          })
          .then(html => {
            // Find the results container
            if (!this.resultsContainer) {
              this.resultsContainer = document.querySelector('.order-status-results') ||
                                    document.querySelector('#order-status-container') ||
                                    document.querySelector('.order-status-details') ||
                                    this;
            }

            // Create a temporary container to parse the HTML
            const tempContainer = document.createElement('div');
            tempContainer.innerHTML = html;

            // Find the order content in the fetched HTML
            let orderContent = tempContainer.querySelector('.order-status-details') ||
                                tempContainer.querySelector('.order-status') ||
                                tempContainer.querySelector('.order-lookup-result');

            if (!orderContent) {
              console.error('[OrderStatusLookup] Order content not found in fetched HTML');
              return;
            }

            // Don't hide loading yet - wait for nested components to be fully hydrated
            this.hideError();

            // Show the results container
            if (this.resultsContainer) {
              this.resultsContainer.classList.remove('hidden');
              this.resultsContainer.classList.add(CLASSES.CONTENT);
              this.resultsContainer.classList.remove(CLASSES.ERROR);
              // Keep LOADING class until components are hydrated
            }

            // Replace the content in the results container
            this.resultsContainer.innerHTML = '';
            this.resultsContainer.appendChild(orderContent);

            // Store basic order data in window.OrderStatus for any components that might need it
            window.OrderStatus = {
              orderNumber: orderData.orderNumber,
              customerEmail: orderData.customerEmail || orderData.email,
              orderDate: orderData.orderCreatedAt || orderData.created_at,
              lastUpdated: new Date().toISOString()
            };

            // Dispatch a custom event that the order content has been updated
            const contentUpdatedEvent = new CustomEvent('order-content:updated', {
              bubbles: true,
              detail: { orderNumber: orderData.orderNumber, content: orderContent }
            });
            this.dispatchEvent(contentUpdatedEvent);

            // Also dispatch a global event for other components
            const orderStatusUpdatedEvent = new CustomEvent('order-status-updated', {
              bubbles: true,
              detail: { orderNumber: orderData.orderNumber, orderData }
            });
            document.dispatchEvent(orderStatusUpdatedEvent);

            // Ensure nested components are initialized
            debug('%c[OrderStatusLookup] Dispatching content-ready event to initialize nested components', 'color: #FF9800; font-weight: bold');
            const contentReadyEvent = new CustomEvent('order-content:ready', {
              bubbles: true,
              detail: { orderNumber: orderData.orderNumber, content: orderContent }
            });
            this.dispatchEvent(contentReadyEvent);

            // Initialize nested components directly
            if (orderContent) {
              debug('orderContent exists, calling _initializeNestedComponents');
              this._initializeNestedComponents(orderContent);
            } else {
              debug('%c[OrderStatusLookup] orderContent not available for initialization', 'color: #F44336; font-weight: bold');
            }
          })
          .catch(error => {
            console.error('[OrderStatusLookup] Error fetching order content:', error);
            this.showError('Failed to load order details. Please try again.');
          });
      } catch (error) {
        console.error('[OrderStatusLookup] Error in _fetchOrderContent:', error);
        this.showError('An unexpected error occurred. Please try again.');
      }
    }

    /**
     * Initialize nested components within the order content
     * @param {HTMLElement} orderContent - The order content element
     * @private
     */
    _initializeNestedComponents(orderContent) {
      if (!orderContent) {
        debug('No order content provided to _initializeNestedComponents');
        return;
      }

      debug('Initializing nested components');

      try {
        // Find MakeYourChoiceDirect components
        const choiceComponents = orderContent.querySelectorAll('.make-your-choice-direct-container');

        if (choiceComponents.length === 0) {
          debug('No MakeYourChoiceDirect components found');
          this.hideLoading();
          return;
        }

        choiceComponents.forEach(component => {
          // Look for job codes and order number data
          const jobCodesScript = component.querySelector('#job-codes-data') ||
                                component.querySelector('script[type="application/json"]');
          const orderNumberScript = component.querySelector('#order-number-data') ||
                                  component.querySelector('script[type="text/plain"]');

          if (jobCodesScript && orderNumberScript) {
            this._waitForMakeYourChoiceDirect().then((componentFound) => {
              try {
                // If component wasn't found, log and return early
                if (!componentFound) {
                  debug('MakeYourChoiceDirect component not found after waiting, skipping initialization');
                  return;
                }

                // Parse the job codes data
                let jobCodes;
                try {
                  jobCodes = JSON.parse(jobCodesScript.textContent);
                  // Handle double-encoded JSON
                  if (typeof jobCodes === 'string') {
                    jobCodes = JSON.parse(jobCodes);
                  }
                } catch (parseError) {
                  debug('Error parsing job codes, using raw text');
                  jobCodes = jobCodesScript.textContent;
                }

                // Parse the order number
                let orderNumber;
                try {
                  orderNumber = JSON.parse(orderNumberScript.textContent);
                } catch (parseError) {
                  debug('Error parsing order number, using raw text');
                  orderNumber = orderNumberScript.textContent;
                }

                // Initialize the component
                if (window.MakeYourChoiceDirect && typeof window.MakeYourChoiceDirect.init === 'function') {
                  debug('Initializing MakeYourChoiceDirect with data:', { jobCodes, orderNumber });
                  window.MakeYourChoiceDirect.init(jobCodes, orderNumber);

                  // Dispatch custom event to trigger DOM refresh after initialization
                  debug('Dispatching make-your-choice:refresh event to refresh DOM');
                  document.dispatchEvent(new CustomEvent('make-your-choice:refresh', {
                    bubbles: true,
                    detail: { orderNumber }
                  }));
                }
              } catch (error) {
                logError('Error initializing MakeYourChoiceDirect:', error);
              }
            }).catch((error) => {
              debug('Error in MakeYourChoiceDirect initialization:', error);
            });
          }
        });

        // Handle custom elements
        const undefinedElements = orderContent.querySelectorAll(':not(:defined)');
        if (undefinedElements.length > 0) {
          const elementPromises = Array.from(undefinedElements).map(element => {
            const tagName = element.tagName.toLowerCase();
            if (window.customElements && window.customElements.whenDefined) {
              return window.customElements.whenDefined(tagName).catch(() => {});
            }
            return Promise.resolve();
          });

          Promise.all(elementPromises).then(() => this.hideLoading());
        } else {
          this.hideLoading();
        }

        // Dispatch event that nested components have been initialized
        this.dispatchEvent(new CustomEvent('order-content:components-initialized', {
          bubbles: true,
          detail: { content: orderContent }
        }));

      } catch (error) {
        logError('Error initializing nested components:', error);
        this.hideLoading();
      }
    }

    /**
     * Check if MakeYourChoiceDirect is available on the window object
     * @returns {Promise} Promise that resolves with boolean indicating if component is available
     * @private
     */
    _waitForMakeYourChoiceDirect() {
      debug('_waitForMakeYourChoiceDirect called');
      return new Promise((resolve) => {
        // Simply check if it's available and resolve immediately
        const isAvailable = window.MakeYourChoiceDirect && typeof window.MakeYourChoiceDirect.init === 'function';
        debug(`MakeYourChoiceDirect available: ${isAvailable}`);
        resolve(isAvailable);
      });
    }

    /**
     * Check URL parameters and auto-submit if present
     */
    checkUrlParametersAndAutoSubmit() {
      const urlParams = new URLSearchParams(window.location.search);
      const email = urlParams.get('email');
      const orderNumber = urlParams.get('order_name') || urlParams.get('order_number');

      if (email && orderNumber) {
        debug('URL parameters detected:', { email, orderNumber });

        // Wait for form to be ready before hiding and triggering lookup
        this.waitForFormAndProcess(email, orderNumber);
      }
    }

    /**
     * Wait for the form to be fully loaded before processing the order lookup
     * @param {string} email - Customer email
     * @param {string} orderNumber - Order number
     */
    waitForFormAndProcess(email, orderNumber) {
      debug('Waiting for form to be ready...');
      this.showLoading();

      // Try to find the form-embed element
      const formEmbed = document.querySelector('shopify-forms-embed');

      if (formEmbed && formEmbed.shadowRoot) {
        // Form is already available, process immediately
        this.hideFormForAutoSubmit();
        this.submitFormWithData(formEmbed, email, orderNumber);
      } else {
        // Wait for form to be available
        let attempts = 0;
        const maxAttempts = 10;

        const checkForm = () => {
          attempts++;
          const formEmbed = document.querySelector('shopify-forms-embed');

          if (formEmbed && formEmbed.shadowRoot) {
            debug('Form found after ' + attempts + ' attempts');
            this.hideFormForAutoSubmit();
            this.submitFormWithData(formEmbed, email, orderNumber);
            return;
          }

          if (attempts >= maxAttempts) {
            debug('Form not found after ' + maxAttempts + ' attempts, proceeding with direct lookup');
            this.processOrderLookup(orderNumber, email);
            return;
          }

          // Try again in 200ms
          setTimeout(checkForm, 200);
        };

        // Start checking
        checkForm();
      }
    }

    /**
     * Submit the form with the provided email and order number
     * @param {HTMLElement} formEmbed - The form-embed element
     * @param {string} email - Customer email
     * @param {string} orderNumber - Order number
     */
    submitFormWithData(formEmbed, email, orderNumber) {
      debug('Submitting form with data:', { email, orderNumber });

      if (!formEmbed || !formEmbed.shadowRoot) {
        debug('Form or shadow root not available for submission');
        this.processOrderLookup(orderNumber, email);
        return;
      }

      try {
        // Wait for shadow DOM to be fully initialized
        setTimeout(() => {
          // Find the form in the shadow root
          const form = formEmbed.shadowRoot.querySelector('form, [data-testid="form"]');
          if (!form) {
            debug('Form not found in shadow root');
            this.processOrderLookup(orderNumber, email);
            return;
          }

          // Find the inputs
          const emailInput = form.querySelector('#email, [data-testid="field-email"]');
          const orderInput = form.querySelector('[id="custom#order_number"], [data-testid="field-custom#order_number"]');

          // Set request ID field if it exists
          const requestIdField = form.querySelector('[id="custom#request_id"]');
          if (requestIdField) {
            requestIdField.value = orderNumber;
            requestIdField.dispatchEvent(new Event('input', { bubbles: true }));
          }

          // Set request field if it exists
          const requestField = form.querySelector('[name="custom#request"]');
          if (requestField) {
            requestField.value = JSON.stringify({ orderNumber, email });
            requestField.dispatchEvent(new Event('input', { bubbles: true }));
          }

          // Set the email and order number fields
          if (emailInput && orderInput) {
            // Set email
            emailInput.value = email;
            emailInput.dispatchEvent(new Event('input', { bubbles: true }));

            // Set order number
            orderInput.value = orderNumber;
            orderInput.dispatchEvent(new Event('input', { bubbles: true }));

            // Store the request data in sessionStorage to prevent infinite loops
            sessionStorage.setItem('pendingOrderLookup', JSON.stringify({
              requestID: orderNumber,
              orderNumber,
              email,
              timestamp: Date.now()
            }));

            // Submit the form
            debug('Submitting form...');
            // Use requestSubmit() which properly triggers validation and listeners:
            if (form.requestSubmit) {
              form.requestSubmit();
            } else {
              // Fallback for older browsers - call the handler directly
              const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
              form.dispatchEvent(submitEvent);
            }

            // Also directly call Flow.request() to start polling
            // since the form submit handler will return early due to sessionStorage
            debug('Starting Flow request after form submission');
            this.showLoading();
            if (!this.flowInstance) {
              this.initializeFlow();
            }

            this.flowInstance.request({
              orderNumber: orderNumber,
              email: email
            })
              .then(orderData => {
                this.showContent(orderData);
              })
              .catch(error => {
                this.showError(error.message || 'Failed to retrieve order status');
              });
          } else {
            debug('Form inputs not found, falling back to direct lookup');
            this.processOrderLookup(orderNumber, email);
          }
        }, 300); // Give shadow DOM a moment to fully initialize
      } catch (error) {
        console.error('[OrderStatusLookup] Error submitting form:', error);
        this.processOrderLookup(orderNumber, email);
      }
    }

    /**
     * Hide the form when auto-submitting via URL parameters
     */
    hideFormForAutoSubmit() {
      // Find and hide the form-embed element
      const formEmbed = document.querySelector('shopify-forms-embed');
      if (formEmbed) {
        debug('Hiding form for auto-submit');
        formEmbed.style.display = 'none';
      }
    }

    checkUrlParametersForExistingForm() {
      const urlParams = new URLSearchParams(window.location.search);
      const email = this.getEmailFromUrlParams(urlParams);
      const orderNumber = urlParams.get('order_number');

      if (email && orderNumber) {
        debug('URL parameters detected for existing form:', { email, orderNumber });

        const formEmbed = document.querySelector('shopify-forms-embed');
        if (formEmbed) {
          this.hideFormForAutoSubmit();
          // Try to populate and submit the form
          this.populateAndSubmitForm(formEmbed, email, orderNumber);
        } else {
          // No form found, trigger lookup anyway
          this.processOrderLookup(orderNumber, email);
        }
      }
    }

    /**
     * Set up listener for make-your-choice-direct component hydration events
     */
    setupMakeYourChoiceDirectListener() {
      debug('Setting up MakeYourChoiceDirect listener');
      document.addEventListener('make-your-choice-direct:hydrated', () => {
        debug('Received make-your-choice-direct:hydrated event');
        this.hideLoading();
      });
    }

    checkUrlParametersForForm(formEmbed) {
      const urlParams = new URLSearchParams(window.location.search);
      const email = this.getEmailFromUrlParams(urlParams);
      const orderNumber = urlParams.get('order_number');

      if (email && orderNumber) {
        debug('URL parameters detected for new form:', { email, orderNumber });

        // Hide the form since we're auto-submitting
        if (formEmbed) {
          formEmbed.style.display = 'none';
        }

        // Try to populate and submit the form
        this.populateAndSubmitForm(formEmbed, email, orderNumber);
      }
    }

    populateAndSubmitForm(formEmbed, email, orderNumber) {
      let attempts = 0;
      const maxAttempts = 100; // Poll for ~1.6 seconds

      const pollForShadowForm = () => {
        const shadowRoot = formEmbed.shadowRoot;
        const emailInput = shadowRoot ? shadowRoot.querySelector('input[name="customer_email"]') : null;
        const orderInput = shadowRoot ? shadowRoot.querySelector('input[name="order_number"]') : null;
        const form = shadowRoot ? shadowRoot.querySelector('form') : null;

        if (emailInput && orderInput && form) {
          // All elements are ready, proceed with populating and submitting
          debug('Populating form with:', { email, orderNumber });
          emailInput.value = email;
          orderInput.value = orderNumber;

          // Create and dispatch a custom event to indicate programmatic submission
          const event = new CustomEvent('programmatic-submit', {
            bubbles: true,
            composed: true,
            detail: { email, orderNumber }
          });
          form.dispatchEvent(event);

          // Submit the form to trigger the Shopify Flow
          setTimeout(() => {
            form.submit();
          }, 100);

        } else if (attempts < maxAttempts) {
          // Elements not ready, poll again on the next frame
          attempts++;
          requestAnimationFrame(pollForShadowForm);
        } else {
          // Max attempts reached - use warning instead of error as this might be expected
          console.warn('[OrderStatusLookup] Form inputs not found in shadow DOM after multiple attempts.');
        }
      };

      pollForShadowForm();
    }

    getEmailFromUrlParams(urlParams) {
      // Get the raw email parameter
      const email = urlParams.get('email');
      if (!email) return null;

      // This handles cases like "test+alias@example.com" in URLs
      return email.replace(/ /g, '+');
    }
  }

  // Register the custom element if it's not already registered
  try {
    if (!customElements.get('order-status-lookup')) {
      customElements.define('order-status-lookup', OrderStatusLookup);
    }
  } catch (error) {
    console.error('[OrderStatusLookup] Error registering custom element:', error);
  }
})();
